import { Checkbox, CheckboxCheckedFilled } from '@carbon/icons-react';
import { Banner } from 'component/banner';
import { Center, HorizBox, HoverView, Pad, WindowTitle } from 'component/basics';
import { CTAButton } from 'component/button';
import {
    colorBorderPrimary,
    colorBorderSelected,
    colorButtonDisabled,
    colorSurfaceBannerInfo,
    colorSurfaceHighlightShade1,
    colorSurfacePrimary,
    colorSurfaceSecondary,
    colorTextDisabled,
    colorTextPrimary,
    colorTextSecondary,
} from 'component/color';
import { ConversationScreen } from 'component/screen';
import { SkeletonText } from 'component/skeleton';
import { Heading, UtilityText } from 'component/text';
import type { ArticleTeaserConfig } from 'feature/teaser/editorial-question';
import { ToolTip } from 'open-modules/zdf/component/tooltip';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { ArticleConfig } from 'structure/article';
import { useIsAdmin } from 'util/admin';
import { useDatastore, useInstanceKey, useModuleObject, useStructureKey } from 'util/datastore';
import { defineModuleMap, defineModuleMapMap } from 'util/moduledata';
import type { Feature, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

export const TeaserPositionFeature: Feature<ArticleConfig & ArticleTeaserConfig> = {
    key: 'positionteaser',
    name: 'Set Teaser Position',
    subscreens: {
        managePosition: TeaserPositionScreen,
    },
    providesConfig: {
        additionalAdminTeaserWidgets: [OpenTeaserPositioningButton],
        additionalAdminButtons: [OpenTeaserPositioningButton],
    },
};
export type ArticleTeaserPositioning = {
    positionKey: string;
    value: boolean;
    from: string;
    time: number;
};

export const moduleMapMapArticleTeaserPosition = defineModuleMapMap<ArticleTeaserPositioning>(
    'PUBLIC',
    'article-teaser-position',
    'articleTeaserPosition',
);
export const moduleMapArticleTeaserPosition = defineModuleMap<Record<string, ArticleTeaserPositioning>>(
    'PUBLIC',
    'article-teaser-position',
    'articleTeaserPosition',
);

function useAllTeaserPositions() {
    const instanceKey = useInstanceKey();
    return useModuleObject(moduleMapArticleTeaserPosition, instanceKey);
}

type TeaserPosition = {
    key: string;
    label: TLabel;
};

const articlePositions = {
    article_main_text_position_after_second_paragraph: {
        key: 'article_main_text_position_after_second_paragraph',
        label: t('After the second paragraph in the main text'),
    },
    article_main_text_position_above_second_subheading: {
        key: 'article_main_text_position_above_second_subheading',
        label: t('Above the second subheading in the main text'),
    },
    article_main_text_position_after_last_paragraph: {
        key: 'article_main_text_position_after_last_paragraph',
        label: t('After the last paragraph in the main text'),
    },
} as const;

export function TeaserPositionScreen() {
    const isAdmin = useIsAdmin();

    const [edited, setEdited] = useState(false);
    const onChange = () => {
        setEdited(true);
    };

    if (!isAdmin) {
        return (
            <Banner>
                <UtilityText color={colorTextPrimary} label={t('Admin Only')} />
            </Banner>
        );
    }

    return (
        <ConversationScreen pad>
            <WindowTitle label={t('Manage teaser positions')} />
            <Heading label={t('Manage teaser positions')} type='large' />
            <Pad size={8} />
            <UtilityText label={t('Pick up to 2 positions')} />
            <Pad size={24} />
            <DocumentWrapper>
                <SkeletonText backgroundColor={colorTextSecondary} height={20} />
                <Pad size={24} />
                <SkeletonParagraph />
                <Pad size={16} />
                <SkeletonParagraph />
                <Pad size={16} />
                <TeaserSelector onSelect={onChange} teaserPosition={articlePositions.article_main_text_position_after_second_paragraph} />
                <SkeletonSpacer />
                <SkeletonHeading />
                <Pad size={16} />
                <SkeletonParagraph />
                <Pad size={16} />
                <TeaserSelector onSelect={onChange} teaserPosition={articlePositions.article_main_text_position_above_second_subheading} />
                <Pad size={16} />
                <SkeletonHeading />
                <Pad size={16} />
                <SkeletonParagraph />
                <SkeletonSpacer />
                <SkeletonParagraph />
                <Pad size={16} />
                <TeaserSelector onSelect={onChange} teaserPosition={articlePositions.article_main_text_position_after_last_paragraph} />
            </DocumentWrapper>
            {edited && (
                <View>
                    <Pad size={8}></Pad>
                    <Banner color={colorSurfaceBannerInfo}>
                        <UtilityText label={t('Reload the article to see the effect')} weight='strong' />
                    </Banner>
                </View>
            )}
            <Pad size={8} />
        </ConversationScreen>
    );
}

function OpenTeaserPositioningButton() {
    const instanceKey = useInstanceKey();
    const structureKey = useStructureKey();
    const datastore = useDatastore();

    return (
        <CTAButton
            label={t('Open teaser positioning')}
            onPress={() => datastore.gotoInstanceScreen({ structureKey, instanceKey, screenKey: 'managePosition', params: {} })}
            type='secondary'
        />
    );
}

export default function DocumentWrapper({ children }) {
    return <View style={styles.container}>{children}</View>;
}

const styles = StyleSheet.create({
    container: {
        borderWidth: 1,
        backgroundColor: colorSurfaceSecondary,
        borderColor: colorBorderPrimary,
        borderRadius: 12,
        padding: 16,
    },
    teaserContainer: {
        borderWidth: 2,
        backgroundColor: colorSurfacePrimary,
        borderColor: colorBorderPrimary,
        borderRadius: 12,
        padding: 16,
        borderStyle: 'dashed',
    },
    teaserContainerSelected: {
        backgroundColor: colorSurfaceHighlightShade1,
        borderColor: colorBorderSelected,
        borderStyle: 'solid',
    },
    teaserContainerDisabled: {
        backgroundColor: colorSurfaceSecondary,
        borderColor: colorButtonDisabled,
        borderStyle: 'solid',
    },
    teaserContainerSelectedDisabled: {
        pointerEvents: 'none',
    },
    teaserContainerIcon: {
        width: 24,
        height: 24,
    },
    spacer: {
        backgroundColor: colorTextSecondary,
        borderRadius: 12,
        width: 4,
        height: 4,
    },
});

function SkeletonParagraph() {
    return (
        <View>
            <SkeletonText backgroundColor={colorTextSecondary} width={'100%'} />
            <Pad size={4} />
            <SkeletonText backgroundColor={colorTextSecondary} width={'60%'} />
            <Pad size={4} />
            <SkeletonText backgroundColor={colorTextSecondary} width={'80%'} />
        </View>
    );
}

function SkeletonHeading() {
    return <SkeletonText backgroundColor={colorTextSecondary} height={15} width={'90%'} />;
}

function SkeletonSpacer() {
    return (
        <Center>
            <Pad size={8} />
            <View style={styles.spacer} />
            <Pad size={4} />
            <View style={styles.spacer} />
            <Pad size={4} />
            <View style={styles.spacer} />
            <Pad size={8} />
        </Center>
    );
}

type TeaserSelectorProps = {
    teaserPosition: TeaserPosition;
    onSelect: (value: boolean) => void;
};

function TeaserSelector({ teaserPosition, onSelect }: TeaserSelectorProps) {
    const datastore = useDatastore();

    const positions = useAllTeaserPositions();
    const activePositions = Object.values(positions ?? {}).filter((p) => p.value);

    const positioning = positions?.[teaserPosition.key];
    const togglePosition = () => {
        datastore.callServerAsync('articleTeaserPosition', 'setArticleTeaserPosition', {
            positionKey: teaserPosition.key,
            value: !positioning?.value,
        });
        onSelect?.(!positioning?.value);
    };

    const limitReached = activePositions.length >= 2 && !positioning?.value;

    return (
        <ToolTip disabled={!limitReached} text={'Maximum of 2 positions allowed'}>
            <HoverView
                disabled={limitReached}
                onPress={togglePosition}
                style={[
                    styles.teaserContainer,
                    positioning?.value ? styles.teaserContainerSelected : null,
                    limitReached ? styles.teaserContainerSelectedDisabled : null,
                ]}
            >
                <TeaserSelectionCard
                    disabled={!positioning?.value && limitReached}
                    teaserPosition={teaserPosition}
                    value={positioning?.value}
                />
            </HoverView>
        </ToolTip>
    );
}

type TeaserSelectorBoxProps = {
    teaserPosition: TeaserPosition;
    value?: boolean;
    disabled?: boolean;
};
function TeaserSelectionCard({ teaserPosition, value = false, disabled = false }: TeaserSelectorBoxProps) {
    const textColor = disabled ? colorTextDisabled : colorTextPrimary;

    return (
        <View>
            <HorizBox center spread>
                <View style={{ flex: 1 }}>
                    <UtilityText color={textColor} label={teaserPosition.label} testID={teaserPosition.key} weight='strong' />
                </View>
                <View>
                    {value ? (
                        <CheckboxCheckedFilled color={textColor} style={styles.teaserContainerIcon} />
                    ) : (
                        <Checkbox color={textColor} style={styles.teaserContainerIcon} />
                    )}
                </View>
            </HorizBox>
        </View>
    );
}
